'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from 'next/image';
import Link from 'next/link';

import type { HousingProperty } from '@eliadmin/core/src/api/housing/housing-types';
import { fetchHousingProperty } from '@eliadmin/core/src/api/housing/housing-api';

import { HousingBadge } from '@eliadmin/core/src/components/housing/housing-badge';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { CgRuler } from 'react-icons/cg';
import { FaStar } from 'react-icons/fa6';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { HiOutlineTicket, HiOutlineUserGroup } from 'react-icons/hi';
import { GrLocation } from 'react-icons/gr';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ButtonIcon } from '@eliadmin/core/src/components/ui/button-icon';
import { LightboxComponent } from '@eliadmin/core/src/components/lightbox/lightbox-component';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function HousingCard({ property }: { property: HousingProperty }) {
  const { t } = useTranslations();
  const isDesktop = useBreakpoint('sm');

  const { isOpen, setIsOpen } = useDisclosure(false);
  const [imagesSorted, setImagesSorted] = useState<{ url: string; name: string }[]>([]);

  const locale = useLocaleStore(s => s.currentLocale);

  const { data: propertyDetail } = useQuery({
    queryKey: ['housing-property-detail', property.slug],
    queryFn: () => fetchHousingProperty({ locale, slug: property.slug }).then(r => r.data),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const handleOpenGallery = (images: any, index: number) => () => {
    if (Array.isArray(images) && index >= 0 && index < images.length) {
      const newImages = [...images.slice(index), ...images.slice(0, index)];
      setImagesSorted(newImages);
      setIsOpen(true);
    } else if (images && !Array.isArray(images)) {
      setImagesSorted([images]);
      setIsOpen(true);
    }
  };

  const allImages = propertyDetail?.images?.length ? propertyDetail.images : [property?.image];

  console.log(propertyDetail);

  return (
    <>
      <LightboxComponent isOpen={isOpen} setIsOpen={setIsOpen} images={imagesSorted} />
      <div
        className={cn(
          'col-span-1 flex w-full flex-col items-start justify-start gap-4 rounded-[24px] border p-3 sm:gap-5 sm:p-4 lg:gap-6 lg:p-6',
          'ea-housing-card',
        )}
      >
        <div className="relative flex h-[232px] w-full flex-col items-start justify-between overflow-hidden rounded-[20px] p-3 sm:h-[280px] sm:p-4 lg:h-[394px] lg:p-6">
          <div className="relative z-10 flex w-full items-start justify-between gap-4">
            <div className="flex flex-wrap items-start justify-start gap-1 sm:gap-2">
              <HousingBadge
                before={
                  <ButtonIcon
                    alt={propertyDetail?.rooms[0]?.housing_type?.name ?? ''}
                    url={`/images/icons/${propertyDetail?.rooms[0]?.housing_type?.id === 1 ? 'dom' : 'byt'}.svg`}
                  />
                }
                name={propertyDetail?.rooms[0]?.housing_type?.name ?? ''}
                backgroundColor="#254124"
                textColor="#FFFFFF"
              />
              <HousingBadge
                name="4.9"
                after={<p className="text-[11px] font-normal text-secondary sm:text-sm">(47)</p>}
                className="border border-border"
                backgroundColor="#FFFFFF"
                textColor="#254124"
                before={<FaStar size={16} className="text-[#EDB000]" />}
              />
            </div>
            <div
              onClick={handleOpenGallery(allImages, 0)}
              className={cn(
                'flex max-h-10 min-h-10 min-w-10 max-w-10 cursor-pointer items-center justify-center rounded-full bg-white transition-all hover:bg-white/80 sm:max-h-[50px] sm:min-h-[50px] sm:min-w-[50px] sm:max-w-[50px]',
                'ea-housing-card__gallery-button',
              )}
            >
              <Image
                src={'/images/icons/gallery.svg'}
                alt={t('text2246')}
                width={isDesktop ? 20 : 18}
                height={isDesktop ? 20 : 18}
                className="shrink-0 object-contain"
              />
            </div>
          </div>
          <div className="relative z-10 flex flex-wrap items-start justify-start gap-1 sm:gap-2">
            <HousingBadge
              before={<CgRuler size={16} className="text-secondary" />}
              name={`${property?.area} m²`}
              backgroundColor="#F2EEE7"
            />
            <HousingBadge
              before={<HiOutlineTicket size={16} className="text-secondary" />}
              name={`${property.price_per_night} €/noc`}
              backgroundColor="#F2EEE7"
            />
            <HousingBadge
              before={<HiOutlineUserGroup size={16} className="text-secondary" />}
              name={`${property?.capacity_total} hostí`}
              backgroundColor="#F2EEE7"
            />
          </div>
          <Image
            src={property?.image?.url}
            alt={resolveImageAlt(property?.image, property?.name)}
            fill
            className="absolute z-0 object-cover object-center"
          />
        </div>
        <div className="flex w-full flex-col items-start justify-start gap-6">
          <div className="flex w-full flex-col items-start justify-start gap-3">
            <h3 className="text-2xl font-bold leading-[100%] tracking-tight text-secondary sm:text-3xl sm:leading-[100%] lg:text-[32px] lg:leading-[100%]">
              {property?.name}
            </h3>
            {!!property.address && (
              <div className="flex items-center justify-start gap-2">
                <GrLocation size={20} className="text-secondary" />
                <p className="text-sm font-bold leading-[100%] tracking-tight text-secondary sm:text-base sm:leading-[100%]">
                  {property.address}
                </p>
              </div>
            )}
          </div>
          <div className="flex w-full flex-col items-start justify-start gap-2 sm:flex-row">
            <Button asChild className="w-full sm:w-fit">
              <Link href={'/rezervacia'}>
                <ButtonIcon alt={t('text538')} url="/images/icons/date-primary.svg" />
                {t('text538')}
              </Link>
            </Button>
            <Button asChild variant={'outline'} className="w-full sm:w-fit">
              <Link href={property.slug}>{t('text792')}</Link>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
